import React, { useState } from "react";
import { ChevronDown, ChevronUp, HelpCircle, Tag } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const groups = [
  {
    title: "For Users",
    subtitle: "Tips for exploring BizNep",
    faqs: [
      {
        q: "1. What is BizNep?",
        a: "BizNep is a platform that helps you discover, connect with, and support local businesses near you. You can explore restaurants, shops, services, and more—all in one place.",
      },
      {
        q: "2. Is BizNep free to use?",
        a: "Yes, BizNep is completely free for users to browse, search, and review businesses.",
      },
      {
        q: "3. How do I find businesses near me?",
        a: "Simply open the app and allow location access. BizNep will show businesses based on your current area or selected city.",
      },
      {
        q: "4. Can I leave reviews or ratings?",
        a: "Currently there is no business review feature. It will be introduced in the next phase.",
      },
      {
        q: "5. How do I report incorrect or fake information?",
        a: "You can contact us directly through the app’s support section.",
      },
      {
        q: "6. Do I need an account to use the app?",
        a: "You can browse without an account, but you’ll need to sign up or log in to post reviews, save favorites, or follow businesses.",
      },
      {
        q: "7. How do I contact a business through the app?",
        a: "Each business profile includes contact information such as phone number, address, website, or social media links.",
      },
    ],
  },
  {
    title: "For Business Owners",
    subtitle: "Listing and growth",
    faqs: [
      {
        q: "8. How can I list my business on BizNep?",
        a: "Go to the “Add Business” section in the app or visit our website to submit your business details. Once approved, your business will appear in search results.",
      },
      {
        q: "9. Is there a cost to list my business?",
        a: "Currently, BizNep offers free listings during our launch phase. Premium features may be introduced later for enhanced visibility.",
      },
      {
        q: "10. How long does it take for my listing to go live?",
        a: "After submission, our team reviews and approves listings within 24–48 hours.",
      },
      {
        q: "11. Can I edit or update my business information?",
        a: "You can reach us through our support page to update any information. Later there will be features that will allow businesses to update themselves.",
      },
      {
        q: "12. How can I promote my business on BizNep?",
        a: "We offer featured listings, sponsored placements, and promotional banners. You can contact us through the app for more details.",
      },
      {
        q: "13. My business already exists on BizNep—how can I claim it?",
        a: "You can reach us through our support page and we will reach out for more information.",
      },
    ],
  },
  {
    title: "General / Technical",
    subtitle: "Support & privacy",
    faqs: [
      {
        q: "14. What countries or regions does BizNep cover?",
        a: "Currently, BizNep focuses on local businesses in Nepal, with plans to expand regionally.",
      },
      {
        q: "15. How do I contact BizNep support?",
        a: "You can reach us via the “Help & Support” section in the app or email us at support@biznep.net.",
      },
      {
        q: "16. Is my data safe on BizNep?",
        a: "Yes, we value your privacy. All personal information is securely stored and handled according to our Privacy Policy.",
      },
      {
        q: "17. How can I delete my account?",
        a: "Your account can be deactivated and it will not be visible to the public. Please reach out to us to remove your account. Your account will be flagged as deleted.",
      },
    ],
  },
];

function FAQItem({ faq, isOpen, onToggle }) {
  return (
    <div
      className={`
        overflow-hidden
        rounded-[12px]
        border
        bg-white
        transition-all
        duration-200
        ${
          isOpen
            ? "border-[#d8e3ef] shadow-[0_4px_14px_rgba(40,70,100,0.07)]"
            : "border-[#e1e7ee] shadow-[0_2px_8px_rgba(40,70,100,0.035)]"
        }
      `}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-[#fbfcfe] sm:px-6"
      >
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={`
              flex h-[34px] w-[34px] shrink-0 items-center justify-center
              rounded-full border
              ${
                isOpen
                  ? "border-[#f5c5c5] bg-[#fff5f5]"
                  : "border-[#edf0f4] bg-[#f8fafc]"
              }
            `}
          >
            <HelpCircle
              size={18}
              strokeWidth={2}
              className="text-[#D32323]"
            />
          </div>

          <span className="text-[16px] font-bold leading-[1.4] text-[#17233b]">
            {faq.q}
          </span>
        </div>

        {isOpen ? (
          <ChevronUp
            size={21}
            strokeWidth={2}
            className="shrink-0 text-[#17233b]"
          />
        ) : (
          <ChevronDown
            size={21}
            strokeWidth={2}
            className="shrink-0 text-[#17233b]"
          />
        )}
      </button>

      {isOpen && (
        <div className="border-t border-[#edf1f5] px-5 pb-5 pt-4 sm:px-6">
          <div className="flex items-start gap-3">
            <HelpCircle
              size={17}
              strokeWidth={2}
              className="mt-[3px] shrink-0 text-[#64748b]"
            />

            <p className="text-[15px] leading-[1.7] text-[#59677c]">
              {faq.a}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function FAQGroup({ group }) {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="mb-12 last:mb-0">
      {/* GROUP HEADER */}
      <div className="mb-5">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#f0b3b3] bg-white px-3 py-[7px]">
          <Tag
            size={15}
            strokeWidth={2}
            className="text-[#D32323]"
          />

          <span className="text-[13px] font-extrabold text-[#D32323]">
            {group.title}
          </span>
        </div>

        <p className="mt-2 text-[14px] text-[#6b7280]">
          {group.subtitle}
        </p>
      </div>

      {/* FAQ ITEMS */}
      <div className="space-y-3">
        {group.faqs.map((faq, index) => (
          <FAQItem
            key={faq.q}
            faq={faq}
            isOpen={openIndex === index}
            onToggle={() =>
              setOpenIndex(openIndex === index ? -1 : index)
            }
          />
        ))}
      </div>
    </section>
  );
}

export default function FAQ() {
  return (
    <div className="min-h-screen bg-white">
      {/* EXISTING WEBSITE NAVBAR */}
      <Navbar />

      {/* FAQ CONTENT */}
      <main className="w-full bg-[#fafbff]">
        <div className="mx-auto w-full max-w-[1100px] px-5 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16">
          {/* PAGE HEADER */}
          <div className="mb-12 border-b border-[#e3e9f0] pb-8">
            <div className="mb-3 flex items-center gap-2 text-[#0758b5]">
              <HelpCircle size={21} strokeWidth={2.2} />

              <span className="text-[14px] font-bold uppercase tracking-[0.08em]">
                FAQs
              </span>
            </div>

            <h1 className="text-[36px] font-extrabold leading-[1.15] tracking-[-1px] text-[#10203b] sm:text-[42px]">
              How can we help?
            </h1>

            <p className="mt-4 max-w-[650px] text-[17px] leading-[1.6] text-[#64748b]">
              Quick answers about using BizNep and managing your business.
            </p>
          </div>

          {/* FAQ GROUPS */}
          <div>
            {groups.map((group) => (
              <FAQGroup
                key={group.title}
                group={group}
              />
            ))}
          </div>
        </div>
      </main>

      {/* EXISTING WEBSITE FOOTER */}
      <Footer />
    </div>
  );
}